import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const callRecordingGdprArticle: BlogArticle = {
  key: BlogArticleKey.CALL_RECORDING_GDPR,
  slug: BlogSlugs.CALL_RECORDING_GDPR,
  categoryId: BlogCategory.CALL_ANALYSIS,
  date: "2026-08-30",
  updated: "2026-09-29",
  readTime: 8,
  locales: {
    fr: {
      title: "Enregistrer les appels de vos clients : ce que dit le RGPD",
      excerpt:
        "La question bloque plus d'équipes que la technique. Ce qu'il faut annoncer, sur quelle base légale s'appuyer, combien de temps garder, et ce que la CNIL recommande.",
      description:
        "Enregistrer un appel commercial est légal en France, sous conditions. Information, base légale, conservation, droits : ce qu'il faut prévoir.",
      ctaTitle: "Centralisez vos échanges clients sans y perdre votre conformité",
      ctaText:
        "Vous enregistrez ou importez l'échange que vous avez annoncé, il rejoint la fiche du client, et vous gardez la main sur ce qui est conservé. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La plupart des équipes qui hésitent à analyser leurs appels ne bloquent pas sur l'outil. Elles bloquent sur une question plus simple et jamais tranchée : est-ce qu'on a le droit d'enregistrer, et à quelles conditions.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La réponse courte est oui. La réponse utile tient en quatre choses à mettre en place, et elles ne coûtent presque rien une fois qu'on sait lesquelles.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Enregistrer n'est pas interdit. Enregistrer sans l'avoir annoncé, sans savoir pourquoi, et sans savoir quand ça s'efface, oui.",
        },
        { type: ArticleBlockType.H2, text: "1. Annoncer avant, pas pendant" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "L'information doit arriver au début de l'échange, avant que quoi que ce soit ne soit capté. C'est la partie la plus visible et la plus simple : une phrase au démarrage de l'appel, ou une mention dans l'invitation quand la réunion est planifiée.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cette phrase doit dire trois choses : que l'appel est enregistré, pourquoi, et où la personne peut en savoir plus. *Cet échange est enregistré pour que je puisse le réécouter et préparer la suite. Vous pouvez me dire si vous préférez que je coupe.* C'est court, ça se dit en huit secondes, et ça règle l'essentiel.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le point à ne pas rater : la personne doit pouvoir refuser sans que l'échange s'arrête. Une annonce qui ne laisse pas le choix n'est pas une information, c'est une formalité.",
        },
        { type: ArticleBlockType.H2, text: "2. Choisir la base légale, et savoir laquelle" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est là que la plupart des articles se contredisent, parce que la réponse dépend de ce que vous faites de l'enregistrement. Le consentement n'est pas toujours obligatoire : il l'est quand l'enregistrement sert des finalités marketing, alors que d'autres bases peuvent tenir quand il sert l'exécution d'un contrat.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "En pratique, pour un appel de vente ou de découverte, le consentement est la base la plus sûre et la plus lisible. Elle a l'avantage d'être vérifiable : la personne a entendu l'annonce et n'a pas demandé de couper.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que vous faites de l'appel", "Ce que ça implique"],
          rows: [
            [
              "Réécouter pour préparer la suite du deal",
              "Une finalité précise, facile à annoncer et à justifier",
            ],
            [
              "En tirer des enseignements sur votre marché",
              "Même finalité si vous l'annoncez, à condition de ne pas rejuger la personne",
            ],
            [
              "Évaluer un commercial de votre équipe",
              "Un traitement qui touche un salarié, avec information et consultation préalables",
            ],
            [
              "Nourrir un modèle ou un profil durable",
              "Une finalité à part entière, qui doit être annoncée comme telle",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La troisième ligne est celle qu'on oublie. Analyser les appels de ses propres commerciaux n'est pas la même chose qu'analyser un marché : ça devient un dispositif d'évaluation, avec ses obligations d'information et de dialogue interne.",
        },
        { type: ArticleBlockType.H2, text: "3. Décider combien de temps vous gardez" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une durée de conservation limitée fait partie des conditions, et les repères qui circulent vont de six mois à cinq ans selon la finalité. Le chiffre compte moins que le fait de l'avoir fixé et de s'y tenir : une durée non décidée est une durée infinie, et c'est elle qui pose problème.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Fixez la durée avant d'enregistrer**, pas le jour où on vous la demande.",
            "**Séparez l'audio de ce que vous en avez tiré.** Le fichier brut est ce qui coûte le plus cher à garder, et souvent le moins utile passé quelques semaines.",
            "**Écrivez-la quelque part que quelqu'un d'autre peut lire.** Une politique de confidentialité, une note interne, peu importe le support.",
            "**Supprimez pour de bon.** Un fichier déplacé dans un dossier d'archive n'est pas supprimé.",
          ],
        },
        { type: ArticleBlockType.H2, text: "4. Pouvoir répondre quand on vous le demande" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une personne enregistrée peut demander à savoir ce que vous détenez sur elle, et à le faire effacer. Ça paraît lourd, ça ne l'est que si vous n'avez rien prévu. Il suffit de pouvoir retrouver les appels d'une personne et de savoir les supprimer.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La CNIL recommande par ailleurs de pseudonymiser avant toute phase d'annotation humaine, et de chiffrer le stockage. Autrement dit : si un humain relit des extraits, il n'a pas besoin de savoir qui parle.",
        },
        { type: ArticleBlockType.H2, text: "Ce que ça change dans le choix d'un outil" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Ce qui complique",
            items: [
              "Un robot qui rejoint la réunion et s'annonce à votre place, ou pas",
              "Des transferts hors Union européenne que personne n'a listés",
              "Des enregistrements conservés par défaut, sans durée annoncée",
              "Aucun moyen de retrouver ce que vous détenez sur une personne",
            ],
          },
          good: {
            title: "Ce qui simplifie",
            items: [
              "Un enregistrement ou un import que vous lancez vous-même, appel par appel",
              "Des sous-traitants et des transferts hors UE listés et encadrés",
              "Une durée de conservation décidée et écrite",
              "Une suppression qui vaut pour tout, y compris ce qui a été extrait",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La différence entre les deux colonnes n'est pas technique, elle est dans qui décide. Un outil qui enregistre à votre place décide pour vous. Un outil où c'est vous qui lancez l'enregistrement, ou qui importez un appel que vous avez choisi d'enregistrer, laisse la décision, et la responsabilité, du bon côté.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "Aucun robot ne rejoint vos réunions. Vous lancez vous-même l'enregistrement d'un appel Google Meet depuis l'extension Chrome, ou vous importez un fichier audio ou vidéo, après avoir prévenu les participants. L'échange rejoint la fiche du client, et vous gardez la main sur ce qui reste. Les sous-traitants et l'encadrement des transferts sont décrits dans la politique de confidentialité, et supprimer un échange efface aussi ce qui en a été extrait.",
          linkLabel: "Voir les fiches clients",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Annoncez au début, sachez pourquoi vous enregistrez, fixez une durée et tenez-la, et gardez le moyen de répondre à une demande. Quatre décisions, prises une fois. C'est nettement moins coûteux que de renoncer à comprendre pourquoi vos appels se passent comme ils se passent.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cet article décrit des principes et n'est pas un avis juridique. Si votre usage sort du cadre commercial classique, en particulier s'il touche à l'évaluation de salariés, faites-le relire.",
        },
      ],
      faq: [
        {
          question: "Peut-on enregistrer un appel commercial sans le dire ?",
          answer:
            "Non. L'information doit arriver avant l'enregistrement, et la personne doit pouvoir refuser sans que l'échange s'arrête. Une annonce faite après coup, ou une mention noyée dans des conditions générales, ne remplit pas cette condition.",
        },
        {
          question: "Faut-il un consentement écrit pour enregistrer un appel ?",
          answer:
            "Pas nécessairement un écrit. Ce qui compte est que la personne ait été informée avant et ait pu s'y opposer. Pour un appel de vente, une annonce claire au début de l'échange, laissée sans objection, est la voie la plus lisible.",
        },
        {
          question: "Combien de temps peut-on conserver un enregistrement d'appel ?",
          answer:
            "Les repères publiés vont de six mois à cinq ans selon la finalité. Le point important est d'avoir fixé une durée, de l'avoir écrite et de supprimer réellement à l'échéance. Une durée non décidée est le vrai risque.",
        },
        {
          question: "Analyser les appels de ses commerciaux, est-ce autorisé ?",
          answer:
            "C'est possible, mais ce n'est plus le même traitement : il touche des salariés et relève de l'évaluation. Il demande une information préalable des personnes concernées et un dialogue interne, et il mérite une relecture juridique avant d'être mis en place.",
        },
        {
          question: "Un outil d'analyse d'appels doit-il héberger les données en Europe ?",
          answer:
            "Ce n'est pas une obligation absolue, mais un hébergement dans l'Union européenne supprime la question des transferts hors UE, qui demande sinon d'être encadrée et justifiée. À usage égal, c'est une contrainte de moins.",
        },
      ],
    },
    en: {
      title: "Recording your customer calls: what the GDPR actually asks for",
      excerpt:
        "This question stops more teams than the technology does. What to announce, which legal basis to rely on, how long to keep the file, and what the regulator recommends.",
      description:
        "Recording a sales call is lawful in Europe, under precise conditions. Notice, legal basis, retention period, individual rights: what you need in place.",
      ctaTitle: "Centralise your customer exchanges without losing compliance",
      ctaText:
        "You record or import the exchange you announced, it joins the customer file, and you keep control of what is stored. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Most teams hesitating to analyse their calls are not stuck on the tool. They are stuck on a simpler question nobody ever settled: are we allowed to record, and on what terms.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The short answer is yes. The useful answer comes down to four things to put in place, and they cost almost nothing once you know which ones.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Recording is not forbidden. Recording without announcing it, without knowing why, and without knowing when it is deleted, is.",
        },
        { type: ArticleBlockType.H2, text: "1. Announce before, not during" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The notice has to come at the start of the exchange, before anything is captured. It is the most visible part and the simplest: one sentence when the call opens, or a line in the invite when the meeting is scheduled.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That sentence must say three things: that the call is recorded, why, and where the person can learn more. *I am recording this so I can listen back and prepare the next step. Tell me if you would rather I stopped.* It takes eight seconds and settles most of it.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The part not to miss: the person must be able to decline without the conversation ending. A notice that leaves no choice is not information, it is paperwork.",
        },
        { type: ArticleBlockType.H2, text: "2. Pick the legal basis, and know which one" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is where most articles contradict each other, because the answer depends on what you do with the recording. Consent is not always required: it is for marketing purposes, while other bases can hold when the recording serves the performance of a contract.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "In practice, for a sales or discovery call, consent is the safest and the most legible basis. It has the advantage of being verifiable: the person heard the notice and did not ask you to stop.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What you do with the call", "What it implies"],
          rows: [
            [
              "Listen back to prepare the next step of the deal",
              "A precise purpose, easy to announce and to justify",
            ],
            [
              "Draw lessons about your market",
              "The same purpose if you announce it, as long as you do not re-judge the person",
            ],
            [
              "Assess a salesperson on your team",
              "A process touching an employee, with prior notice and internal consultation",
            ],
            [
              "Feed a model or a lasting profile",
              "A purpose of its own, which has to be announced as such",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The third row is the one people forget. Analysing your own reps' calls is not the same as analysing a market: it becomes an assessment mechanism, with its own duties of notice and internal dialogue.",
        },
        { type: ArticleBlockType.H2, text: "3. Decide how long you keep it" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A limited retention period is part of the conditions, and the published guidance ranges from six months to five years depending on the purpose. The number matters less than having fixed one and sticking to it: a period nobody decided is an unlimited one, and that is what creates the problem.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Set the period before recording**, not the day someone asks for it.",
            "**Separate the audio from what you drew out of it.** The raw file is the most expensive thing to keep, and often the least useful after a few weeks.",
            "**Write it somewhere another person can read.** A privacy policy, an internal note, the medium does not matter.",
            "**Delete for real.** A file moved to an archive folder is not deleted.",
          ],
        },
        { type: ArticleBlockType.H2, text: "4. Be able to answer when asked" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A recorded person can ask what you hold about them, and ask you to erase it. That sounds heavy; it only is if you planned nothing. You need to be able to find a person's calls and to delete them.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The French regulator also recommends pseudonymising before any human annotation phase, and encrypting storage. Put plainly: if a human reads excerpts back, they do not need to know who is speaking.",
        },
        { type: ArticleBlockType.H2, text: "What it changes when choosing a tool" },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "What complicates things",
            items: [
              "A bot that joins the meeting and announces itself for you, or does not",
              "Transfers outside the European Union that nobody has listed",
              "Recordings kept by default, with no stated period",
              "No way to find what you hold about one person",
            ],
          },
          good: {
            title: "What simplifies things",
            items: [
              "A recording or an import you start yourself, call by call",
              "Sub-processors and transfers outside the EU listed and safeguarded",
              "A retention period decided and written down",
              "A deletion that covers everything, including what was extracted",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The difference between the two columns is not technical, it is about who decides. A tool that records for you decides for you. A tool where you start the recording yourself, or import a call you chose to record, leaves the decision, and the responsibility, on the right side.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "No bot joins your meetings. You start recording a Google Meet call yourself from the Chrome extension, or you import an audio or video file, after telling the participants. The exchange joins the customer file, and you keep control of what remains. Sub-processors and how transfers are safeguarded are described in the privacy policy, and deleting an exchange also erases what was extracted from it.",
          linkLabel: "See the customer files",
          linkPath: PublicPaths.HOME,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Announce at the start, know why you are recording, set a period and hold to it, and keep the means to answer a request. Four decisions, taken once. Far cheaper than giving up on understanding why your calls go the way they go.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This article describes principles and is not legal advice. If your use goes beyond the ordinary sales case, in particular if it touches employee assessment, have it reviewed.",
        },
      ],
      faq: [
        {
          question: "Can you record a sales call without saying so?",
          answer:
            "No. The notice has to come before the recording, and the person must be able to decline without the exchange ending. A notice given afterwards, or buried in terms and conditions, does not meet that condition.",
        },
        {
          question: "Do you need written consent to record a call?",
          answer:
            "Not necessarily in writing. What matters is that the person was informed beforehand and could object. For a sales call, a clear notice at the start of the exchange, left unopposed, is the most legible route.",
        },
        {
          question: "How long can a call recording be kept?",
          answer:
            "Published guidance ranges from six months to five years depending on the purpose. The important part is to have fixed a period, written it down, and to actually delete when it expires. An undecided period is the real risk.",
        },
        {
          question: "Is analysing your own salespeople's calls allowed?",
          answer:
            "It is possible, but it is no longer the same processing: it touches employees and counts as assessment. It calls for prior notice to the people concerned and internal dialogue, and it deserves a legal review before being put in place.",
        },
        {
          question: "Does a call analysis tool have to host data in Europe?",
          answer:
            "It is not an absolute obligation, but hosting in the European Union removes the question of transfers outside the EU, which otherwise has to be framed and justified. For the same use, that is one constraint fewer.",
        },
      ],
    },
  },
};
